import { Divider, Flex, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { splitFullName } from '@dealstack/shared/lib/format/user';
import { UserInitialsBadge } from '@dealstack/frontend/shared/components/UserInitialsBadge/UserInitialsBadge.tsx';
import { SecurityPill } from '@dealstack/frontend/shared/components/SecurityPill/SecurityPill.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { TaxRecordItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/TaxRecordsCard/TaxRecordItem.tsx';
import { RelatedPersonCardRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeople.types.ts';
import { getRoleLabel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeople.utils.ts';
import { RelatedPersonCardStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/RelatedPeopleCards.styles.tsx';

interface Props {
	person: RelatedPersonCardRow;
	investorPath: string | null;
}

export const RelatedPersonCard = ({ person, investorPath }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { firstName, lastName } = splitFullName(person.displayName);
	const [taxRecord, ...otherTaxRecords] = person.taxResidence;

	return (
		<RelatedPersonCardStyles
			{...(investorPath ? { as: Link, to: investorPath } : {})}
			data-testid={makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_RELATED_PERSON,
				person.id
			)}
		>
			<div className="rp-header">
				<UserInitialsBadge
					firstName={firstName}
					lastName={lastName}
					size="2rem"
					textVariant="md"
				/>

				<div className="names">
					<Text fz={14} fw={600} c="dark.9" truncate>
						{person.displayName}
					</Text>

					<Text fz={12} c="dark.5" truncate>
						{person.email}
					</Text>
				</div>
			</div>

			<Flex mt="0.25rem" gap="0.25rem" wrap="wrap">
				{person.roles.map((role) => (
					<SecurityPill key={role.relationId} label={getRoleLabel(role, tMipManage)} />
				))}
			</Flex>

			<Divider mt="0.5rem" mb="0.5rem" />

			<div className="tax">
				<div className="tax-header">
					<Text fz={12} fw={600} c="gray.9">
						{tMipManage('investors.details.related_people.tax_information')}
					</Text>

					{otherTaxRecords.length > 0 && (
						<Text fz={12} c="dark.8" className="more">
							{`+${otherTaxRecords.length}`}
						</Text>
					)}
				</div>

				{taxRecord ? (
					<TaxRecordItem taxRecord={taxRecord} />
				) : (
					<Text fz={12} c="dark.0">
						{tMipManage('investors.details.related_people.no_tax_information')}
					</Text>
				)}
			</div>
		</RelatedPersonCardStyles>
	);
};
